// App.js — 앱 진입점
// 36차: notifyExpiringPoints / notifyUnreviewedReservations 클라이언트 직접 호출 제거
//        → Cloud Functions(httpsCallable)로 이관
// 44차: ErrorBoundary 적용 / console.log 제거 / 딥링크 누락 타입 추가
// 46차: Sentry 연동 / 디버그용 ErrorUtils 핸들러 제거
// 72차: 알림 탭 딥링크에 new_review/new_reservation/reservation_cancelled_by_customer/
//       digest_summary 케이스 추가 (기존 'StoreAdmin' route는 72차 탭 분리로 이미
//       없어진 이름이라 무의미했음 — 정리)
// 74차: 예약 리마인더(reservation_reminder) 딥링크 케이스 추가 —
//       functions/index.js의 scheduledReservationReminder가 생성하는 알림용
// 75차: inquiry_replied 딥링크 케이스 추가 — 문의 답변 시 알림이 아예 없던 버그 수정
//       (functions/index.js의 onInquiryReplied 신규)
// 78차 32차: RotateDeviceOverlay 추가 — 가로 모드가 기본인 태블릿 등에서 세로 고정
//       레이아웃이 깨져 보일 때 세로로 돌려달라는 안내를 전체화면으로 덮어 보여줌
// 오늘 fork 재검토(다양한 기기 UI 겹침 확인)로 제거: Fold 펼친 화면(거의 정사각형)에서
//       실제로는 세로(width<height)인데도 useWindowDimensions()가 순간적으로 다른 값을
//       보고해 이 오버레이가 앱 전체를 막아버리는 false positive가 실기기(에뮬레이터)로
//       확인됐음. 애초에 이 오버레이가 막으려던 시나리오(태블릿 가로기본+세로고정 레터박스)는
//       78차33차 실기기 검증에서 이미 안드로이드 OS가 자체적으로 정상 처리하는 걸 확인해서
//       한 번도 실제로 발동할 필요가 없었던 방어 코드였음 — 방어 목적은 검증도 안 되고
//       Fold에서 오탐만 일으키는 지금, 컴포넌트 자체를 삭제(RotateDeviceOverlay.js도 삭제).
// 78차 37차: expo-splash-screen 도입 — preventAutoHideAsync()로 네이티브 스플래시
//       자동 숨김을 막고, JS 스플래시(SplashLoadingScreen)가 첫 프레임을 그린 직후에만
//       hideAsync()를 호출 — 네이티브 스플래시가 사라지고 JS가 따라잡기 전까지 잠깐
//       화면이 비거나 다른 로고가 보이던 "번쩍임"을 제거. NavigationContainer에도 다크
//       테마(배경 COLORS.bg)를 명시 — 기본 DefaultTheme(흰 배경)가 스플래시→홈 전환 순간
//       한 프레임 하얗게 비치던 것이 두 번째 "번쩍임" 신고의 실제 원인이었음.
// 78차 39차: AppAlertHost 마운트 — Alert.alert(OS 네이티브 팝업, 흰 배경)를 앱 톤(다크+골드)에
//       맞춘 커스텀 모달로 대체하기 위한 표시 전담 컴포넌트. 이 컴포넌트 하나만 마운트해두면
//       다른 파일에서 showAlert()를 命令형으로 호출해서 뜨는 구조(AppAlert.js 참고).
// 78차 39차: reservation_cancelled_by_store 딥링크 케이스 누락 발견/추가 — 서버는 이미
//       이 타입으로 알림을 만들고 있는데(매장이 예약을 취소했을 때 고객에게) 탭 시
//       default로 빠져 아무 반응이 없었음.
import 'react-native-gesture-handler';
import React, { useEffect, useRef, useState } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { NavigationContainer, DefaultTheme } from '@react-navigation/native';
import { StatusBar } from 'expo-status-bar';
import { AppState, Platform, View, StyleSheet, useWindowDimensions } from 'react-native';
import { httpsCallable } from 'firebase/functions';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import * as SplashScreen from 'expo-splash-screen';
import * as ExpoLinking from 'expo-linking';
import {
  SpaceGrotesk_500Medium, SpaceGrotesk_600SemiBold, SpaceGrotesk_700Bold,
} from '@expo-google-fonts/space-grotesk';

import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import AppNavigator from './src/navigation';
import { registerPushToken, setupNotificationListeners } from './src/services/notificationService';
import SplashLoadingScreen, { useMinSplashHold } from './src/components/common/SplashLoadingScreen';
import AppAlertHost from './src/components/common/AppAlert';
import { functions } from './src/services/firebase';
import { parseProductId } from './src/services/productService';
import { COLORS } from './src/constants/theme';

SplashScreen.preventAutoHideAsync().catch(() => {});

const navTheme = {
  ...DefaultTheme,
  colors: { ...DefaultTheme.colors, background: COLORS.bg, card: COLORS.bg, border: COLORS.border, text: COLORS.text },
};
import ErrorBoundary from './src/components/common/ErrorBoundary';

// ── Sentry 초기화 ────────────────────────────────────────────
Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  // 프로덕션에서만 활성화 (개발 중 노이즈 방지)
  enabled: !__DEV__,
  // 성능 트레이싱: 트랜잭션의 20% 샘플링
  tracesSampleRate: 0.2,
  // 에러의 100% 수집
  sampleRate: 1.0,
});

// Cloud Functions 참조
const cfNotifyExpiringPoints         = httpsCallable(functions, 'notifyExpiringPoints');
const cfNotifyUnreviewedReservations = httpsCallable(functions, 'notifyUnreviewedReservations');

// 80차 후속: [버그 수정, 근본원인] App()의 폰트로딩 게이트가 clear된 시점에 AppInner가
// AuthProvider 안에 새로 마운트되고, 그 시점에 AppInner 자신의 authLoading/minSplashHold
// 게이트가 아직 안 풀렸으면 여기서 또 새 SplashLoadingScreen 인스턴스가 만들어졌음(서로
// 다른 트리 위치라 React가 언마운트+리마운트) — introPlayed 플래그로 슬라이드 애니메이션
// 재생은 막았지만, 컴포넌트/DOM 자체가 통째로 갈아끼워지는 건 못 막아 웹에서 미세한
// 깜빡임(재신고)으로 남아있었음. 게다가 이 전환은 App()이 그제서야 WebFrame(데스크톱
// 75% 폭 제한)으로 감싸기 시작하는 시점과도 겹쳐서, 스플래시가 전체폭→75%폭으로
// 폭 자체가 변하는 더 눈에 띄는 점프까지 함께 있었음. 폰트로딩 게이트를 AppInner 밖으로
// 빼지 않고, 대신 App()이 fontsReady를 prop으로 넘겨 AppInner가 모든 게이트(폰트+인증
// +최소노출시간)를 단 하나의 조건/단 하나의 리턴 지점에서 처리하도록 통합 — App()은
// 이제 fontsLoaded 여부와 무관하게 항상 같은 트리(WebFrame 포함)를 렌더하므로, 부팅
// 전체 구간에서 SplashLoadingScreen이 정확히 한 번만 마운트되고 폭도 처음부터 끝까지
// 동일하게 유지됨.
function AppInner({ fontsReady }) {
  const { user, profile, loading: authLoading } = useAuth();
  // 80차 후속: 앞의 폰트로딩 게이트와 별개로, 인증 로딩이 이 게이트를 통과하는 시점도
  // 앱 부팅 후 최소 3초(APP_BOOT_TIME 기준 공유)는 지나야 함 — PICK 광고 노출 시간 확보.
  // 80차 후속: 한때 5초로 늘렸었으나(광고 이미지 네트워크 지연 대응), 이후 (1) PICK
  // 광고 쿼리 병렬화, (2) 광고 박스 상시노출 등으로 체감 지연이 줄어 사용자가 3초면
  // 충분하다고 판단 — 다시 3초로.
  // 오늘 웹페이지 개선 요청으로 추가: 웹(매장 유치 메일로 들어오는 데모 링크)은 첫인상
  // 속도가 더 중요해서 1.2초로 단축 — 네이티브 앱은 PICK 광고 노출 목적이 여전히 유효해
  // 3초 그대로 유지.
  const minSplashHold = useMinSplashHold(Platform.OS === 'web' ? 1200 : 3000);
  const adminRole     = profile?.adminRole;
  const navigationRef = useRef(null);
  const appState      = useRef(AppState.currentState);
  // 79차: [버그 수정] 콜드스타트(앱 완전 종료 후 푸시 탭)로 재기동되면 setupNotificationListeners
  // 이펙트(authLoading 게이트보다 먼저 선언돼 있어 항상 즉시 마운트됨)의 getLastNotificationResponseAsync()가
  // Firebase Auth 초기화가 끝나기 전에 먼저 resolve될 수 있는데, 그러면 NavigationContainer 자체가
  // 아직 렌더되지 않아 navigationRef.current가 null이라 딥링크가 조용히 버려지고 있었음(전수
  // 정독으로 발견) — nav가 준비 안 됐으면 데이터를 잠시 보관했다가 NavigationContainer의
  // onReady 시점에 재생.
  const pendingDeepLinkRef = useRef(null);

  useEffect(() => {
    if (user?.uid) registerPushToken(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const runOnFocus = () => {
      // 52차: 노쇼 만료(processExpiredRecruits)·포인트 만료(expirePoints)는
      //   Cloud Functions 스케줄러로 이관 (인수인계 #4).
      //   scheduledExpireRecruits(매일 03:00 KST) / scheduledExpirePoints(매일 04:00 KST)
      //   → 본인 uid·포그라운드 의존 제거로 앱 미실행 유저 누락 해소.

      // 36차: 관리자 제외, Cloud Functions 호출
      if (!adminRole) {
        cfNotifyExpiringPoints({ uid: user.uid }).catch(() => {});
        cfNotifyUnreviewedReservations({ uid: user.uid }).catch(() => {});
      }
    };

    runOnFocus();

    const subscription = AppState.addEventListener('change', nextState => {
      if (appState.current.match(/inactive|background/) && nextState === 'active') {
        runOnFocus();
      }
      appState.current = nextState;
    });

    return () => subscription.remove();
  }, [user?.uid, adminRole]);

  // 79차: 딥링크 처리 로직을 별도 함수로 분리 — 알림 탭 즉시 처리와, nav가 늦게 준비돼
  // 재생하는 경로(onReady) 양쪽에서 재사용.
  const handleDeepLink = (nav, data) => {
    if (!nav || !data?.type) return;

    switch (data.type) {
          // 72차: 매장관리자 알림 — new_review는 리뷰 답글 화면으로 바로 딥링크,
          // 나머지(새 예약/취소/일일요약)는 nav.navigate('Main')으로 탭 네비게이터에 진입만 시킴
          // (StoreAdminOps 등 중첩 탭까지 직접 딥링크하려면 부모 네비게이터 route params가
          //  더 필요해 이번 범위에서는 진입까지만 처리 — NotificationsScreen 목록에서
          //  나머지 세부 이동은 이미 가능함)
          case 'new_review':
            if (data.reviewId) nav.navigate('ReplyToReview', { reviewId: data.reviewId });
            else nav.navigate('Main');
            break;
          // 78차 66차: 새 예약/고객 취소는 운영관리(예약 관리) 탭까지 바로 진입(기존엔
          // 'Main'만 호출해 매장관리 대시보드 탭에 떨어진 뒤 직접 운영관리 탭을 찾아야 했음)
          case 'new_reservation':
          case 'reservation_cancelled_by_customer':
            nav.navigate('Main', { screen: 'StoreAdminOps' });
            break;
          case 'digest_summary':
            nav.navigate('Main');
            break;

          // 포인트
          case 'point_expiry':
            nav.navigate('PointHistory');
            break;

          // 리뷰 리마인더
          case 'review_reminder':
            nav.navigate('MyReviews');
            break;

          // 44차 추가: 즐겨찾기 이벤트 알림 → 공지/소식 화면
          case 'favorite_event':
            nav.navigate('Events');
            break;

          // 78차 49차: favorite_reopen/favorite_price_change 케이스 자체가 없어서 콜드
          // 스타트 상태(앱 완전 종료)에서 푸시를 탭하면 default로 빠져 아무 반응이 없던
          // 버그(NotificationsScreen.js 안에서 목록을 탭할 때만 정상 동작하고 있었음).
          // NotificationsScreen.js와 동일한 이동 규칙(가격변경→테마 상세, 재개장→매장 상세).
          case 'favorite_price_change':
            if (data.productId) nav.navigate('ThemeDetail', { product: { id: data.productId } });
            else nav.navigate('Events');
            break;
          case 'favorite_reopen':
            if (data.storeId) nav.navigate('StoreDetail', { storeId: data.storeId });
            else nav.navigate('Events');
            break;

          // 모집 관련 알림 → 해당 모집 상세 또는 목록
          // 52차: 실제 생성 type 정합성 교정
          //   recruit_applied(신청 도착) / recruit_approved(수락) / recruit_rejected(거절)
          //   recruit_open/accepted/cancelled 는 레거시 호환 위해 유지
          case 'recruit_applied':
          case 'recruit_approved':
          case 'recruit_rejected':
          case 'recruit_open':
          case 'recruit_accepted':
          case 'recruit_cancelled':
          // 80차 후속: 모집 작성자가 그룹 예약을 취소하면 참가자들에게 알림 — recruitId가
          // 항상 있어 위와 동일한 이동 대상.
          case 'recruit_bulk_cancelled':
          // 80차 계속: 참여인원 자리 양도 완료 → 나가는 사람에게(들어오는 사람은
          // 기존 recruit_approved로 이미 커버됨).
          case 'recruit_seat_transferred':
          // 80차 계속: 관심 지역·장르 알림 → 해당 모집글로.
          case 'recruit_genre_match':
          // 80차 계속: 내 모집글에 새 댓글 → 해당 모집글로(작성자 재방문 유도).
          case 'recruit_comment':
            if (data.recruitId) {
              nav.navigate('RecruitDetail', { recruitId: data.recruitId });
            } else {
              // 79차: [버그 수정] 'Recruit'는 GeneralTabNavigator 안에 nested된 탭
              // 이름이라 bare navigate로는 못 찾음(이 세션에서 여러 번 발견된 패턴) —
              // 현재는 recruitId 없이 이 케이스로 오는 서버 알림이 없어 도달 불가능한
              // 상태지만, 레거시 호환 목적의 폴백이라 형태만이라도 맞춰둠.
              nav.navigate('Main', { screen: 'Recruit' });
            }
            break;

          // 80차 계속: recruitShareCF(웹 공유 페이지)의 "UNLOCK에서 참가 신청하기"
          // 버튼이 ?recruit={id}를 달고 오는 것을 받아 그 모집글로 바로 이동 — 지금까지
          // 공유 링크를 눌러도 항상 홈으로만 갔던 것(themeShareCF와 동일한 기존 제약)을
          // 이 딥링크 하나만 좁게 메움. 아래 부팅 시 웹 쿼리파라미터 파싱 effect에서만
          // 만들어지는 타입이라 recruitId가 항상 존재.
          case 'view_recruit':
            nav.navigate('RecruitDetail', { recruitId: data.recruitId });
            break;

          // 80차 계속: 크루 메시지 @멘션/@전체 호출 — 특정 메시지로 딥링크할 수단이
          // 없어(crewPosts는 개별 화면이 아니라 크루 홈 피드 전체) 크루 탭 진입까지만.
          case 'crew_mention':
            nav.navigate('Main', { screen: 'Crew' });
            break;

          // 80차 계속: 친구 초대 성공 알림 → 초대 화면(재공유 유도)
          case 'referral_success':
            nav.navigate('InviteFriend');
            break;

          // 80차 계속: 휴면 유저 재참여 넛지 → 모집 게시판으로
          case 'dormant_nudge':
            nav.navigate('Main', { screen: 'Recruit' });
            break;

          // 81차: 자매 앱 "우리달력"(커플 캘린더)의 제휴 카드 딥링크
          // (roomescape://theme?id=...&from=uricalendar) → 테마 상세. ThemeDetailScreen은
          // { id }만 받아도 getProductById로 다시 조회하고 삭제된 테마는 notFound 처리함
          // (78·79차에 이미 만들어진 경로 재사용 — 신규 분기 불필요).
          case 'view_theme':
            nav.navigate('ThemeDetail', { product: { id: data.productId } });
            break;

          // 52차 추가: 매장 운영 종료 알림 → 예약 내역 확인
          case 'store_closed':
            nav.navigate('ReservationHistory');
            break;

          // 74차 추가: 예약 리마인더(24시간/3시간 전) 알림 → 예약 내역 확인
          // 78차 39차: reservation_cancelled_by_store(매장이 취소) 케이스가 아예 없어서
          // 탭해도 default로 빠져 아무 반응이 없던 버그 발견 — 같은 "예약 내역 확인"
          // 패턴으로 추가(store_closed/reservation_reminder와 동일).
          case 'reservation_reminder':
          case 'reservation_cancelled_by_store':
            nav.navigate('ReservationHistory');
            break;

          // 신고 결과 / 노쇼 제재 알림 → 알림 화면
          // 52차: noshow_penalty(실제 생성) 추가, sanction 은 레거시 호환 유지
          case 'report_result':
          case 'noshow_penalty':
          case 'sanction':
            nav.navigate('Notifications');
            break;

          // 75차 추가: 새 앱 문의(총괄관리자 전용) → 앱 문의 목록 화면
          case 'new_inquiry':
            nav.navigate('Inquiries', { mode: 'app' });
            break;

          // 78차 49차: 새 매장 문의(매장관리자 전용) 케이스 누락 — NotificationsScreen.js엔
          // 이미 있었는데(78차66차) App.js엔 처음부터 빠져있어 콜드스타트 푸시 탭이 무반응이었음.
          case 'new_store_inquiry':
            nav.navigate('Inquiries', { mode: 'store' });
            break;

          // 75차 추가: 내가 쓴 문의에 답변이 달렸을 때 → 내 문의 내역 화면
          case 'inquiry_replied':
            nav.navigate('MyInquiries');
            break;

          // 79차: 문의자 후속 메시지(재오픈) → 담당 관리자에게, storeId 유무로 구분
          case 'inquiry_followup':
            nav.navigate('Inquiries', { mode: data.storeId ? 'store' : 'app' });
            break;

          // 75차 추가: 3일 넘게 미처리된 신고 요약(총괄관리자 전용) → 유저·신고 관리 화면
          // 79차: [버그 수정] SuperUserReport는 top-level AppStack 화면이 아니라
          // SuperAdminTabNavigator 안에 nested된 tab이라 bare navigate가 안 먹음
          // (FavoritesScreen.js가 78차에 겪었던 것과 동일한 이유, 바로 아래
          // contract_expiring 케이스는 이미 올바른 형태로 돼 있었음) — 콜드스타트
          // 상태에서 이 푸시를 탭하면 무반응이었을 것으로 추정(전수 정독으로 발견).
          case 'report_overdue':
            nav.navigate('Main', { screen: 'SuperUserReport' });
            break;

          // 78차: 외부채널 예약 수동입력 리마인드(매장관리자 전용) → 예약 관리 진입
          case 'manual_entry_reminder':
            nav.navigate('Main');
            break;

          // 78차 32차 추가: 계약 만료 임박 안내(총관리자 전용) → 매장관리 탭
          case 'contract_expiring':
            nav.navigate('Main', { screen: 'SuperStores' });
            break;

          // 78차 39차 추가: 월별 정산 인보이스 준비 안내(매장관리자 전용) — 세부 탭까지는
          // 딥링크하지 않고 진입만(다른 매장관리자 알림들과 동일 패턴)
          case 'monthly_invoice_ready':
            nav.navigate('Main');
            break;

          default:
            break;
    }
  };

  useEffect(() => {
    const cleanup = setupNotificationListeners({
      onNotification: () => {
        // 수신 로그 제거 (프로덕션)
        // 필요 시 Sentry / 외부 로거로 교체
      },
      onNotificationResponse: ({ data }) => {
        const nav = navigationRef.current;
        // 79차: nav가 아직 없으면(콜드스타트, authLoading 중이라 NavigationContainer
        // 미마운트) 조용히 버리지 않고 보관 — onReady에서 재생.
        if (!nav) { pendingDeepLinkRef.current = data; return; }
        handleDeepLink(nav, data);
      },
    });
    return cleanup;
  }, []);

  // 80차 계속: recruitShareCF(웹 공유 페이지)에서 "?recruit={id}"를 달고 들어온 웹
  // 진입을 감지해 해당 모집글로 딥링크 — NavigationContainer의 linking(URL↔화면 완전
  // 자동동기화)은 78차41차에 다른 버그로 이미 꺼둔 상태라 다시 켜지 않고, 부팅 시
  // 쿼리파라미터를 1회만 직접 파싱해 기존 푸시알림 딥링크 메커니즘(pendingDeepLinkRef→
  // onReady 재생)에 그대로 태우는 좁은 범위의 우회. 네이티브에선 window가 없어 즉시
  // 종료, 웹에서만 동작.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    let recruitId;
    try {
      recruitId = new URLSearchParams(window.location.search).get('recruit');
    } catch { return; }
    if (!recruitId) return;
    const data = { type: 'view_recruit', recruitId };
    const nav = navigationRef.current;
    if (nav) handleDeepLink(nav, data);
    else pendingDeepLinkRef.current = data;
  }, []);

  // 81차: 네이티브 전용 — 외부 앱(우리달력 등)이 roomescape://theme?id=...&from=... 로
  // 앱을 열었을 때 처리. NavigationContainer의 linking 옵션(URL↔화면 완전 자동동기화)은
  // 78차41차에 다른 버그로 꺼둔 상태라 다시 켜지 않고, 위 웹 쿼리파라미터 처리와 동일하게
  // "좁은 범위 우회"로 처리 — host가 'theme'이고 id가 parseProductId를 통과할 때만
  // 반응하고 그 외 URL(카카오·네이버 로그인 콜백 등)은 전부 무시.
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const tryHandle = (url) => {
      if (!url) return;
      let parsed;
      try { parsed = ExpoLinking.parse(url); } catch { return; }
      if (parsed.hostname !== 'theme') return;
      const productId = parsed.queryParams?.id;
      if (!productId || !parseProductId(productId)) return;
      const data = { type: 'view_theme', productId, from: parsed.queryParams?.from || null };
      const nav = navigationRef.current;
      if (nav) handleDeepLink(nav, data);
      else pendingDeepLinkRef.current = data;
    };

    ExpoLinking.getInitialURL().then(tryHandle).catch(() => {});
    const sub = ExpoLinking.addEventListener('url', ({ url }) => tryHandle(url));
    return () => sub.remove();
  }, []);

  if (!fontsReady || authLoading || !minSplashHold) {
    return <SplashLoadingScreen />;
  }

  return (
    // 78차 41차: 웹에서 브라우저 탭 제목이 "UNLOCK"(index.html 기본값) 대신 현재 화면
    // 라우트 이름("Home" 등)으로 바뀌어 있던 버그 — React Navigation의 웹 어댑터가
    // linking 설정 없이도 기본으로 document.title을 라우트 이름과 동기화하는 것이 원인.
    // URL 동기화(enabled)는 그대로 끄고, 탭 제목 동기화만 꺼서 index.html의 정적
    // "UNLOCK" 제목이 계속 유지되게 함.
    <NavigationContainer
      ref={navigationRef}
      theme={navTheme}
      documentTitle={{ enabled: false }}
      // 79차: 콜드스타트 알림 탭이 authLoading 중에 도착해 보관해둔 딥링크를,
      // 네비게이터가 실제로 navigate()를 받을 수 있게 된 시점(onReady)에 재생.
      onReady={() => {
        if (pendingDeepLinkRef.current) {
          handleDeepLink(navigationRef.current, pendingDeepLinkRef.current);
          pendingDeepLinkRef.current = null;
        }
      }}
    >
      <StatusBar style="light" backgroundColor="#121212" />
      <AppNavigator />
      <AppAlertHost />
    </NavigationContainer>
  );
}

export default function App() {
  // 72차: 디자인 시스템 전면 개편 — Pretendard(국문 본문) + Space Grotesk(브랜드/숫자) 로드.
  // 로드 전에는 기존 시스템 폰트로 렌더되다가 갑자기 폰트가 바뀌는 깜빡임을 막기 위해
  // 로드 완료까지 스피너만 보여준다(authLoading과 동일한 패턴).
  const [fontsLoaded] = useFonts({
    'Pretendard-Regular':  require('./assets/fonts/Pretendard-Regular.otf'),
    'Pretendard-Medium':   require('./assets/fonts/Pretendard-Medium.otf'),
    'Pretendard-SemiBold': require('./assets/fonts/Pretendard-SemiBold.otf'),
    'Pretendard-Bold':     require('./assets/fonts/Pretendard-Bold.otf'),
    SpaceGrotesk_500Medium,
    SpaceGrotesk_600SemiBold,
    SpaceGrotesk_700Bold,
  });

  // 78차 35차: 실기기에서 스플래시가 거의 안 보이고 바로 넘어간다는 신고 — 폰트/인증
  // 로딩이 실기기에선 대부분 수십~수백ms 안에 끝나(로컬 캐시) 스플래시 컴포넌트가
  // 사실상 한 프레임만 그려지고 사라지는 것이 원인. 실제 로딩 상태와 무관하게 앱 시작
  // 시점부터 최소 2초는 스플래시가 유지되도록 타이머 게이트 추가(로딩이 2초보다 길면
  // 기존처럼 로딩 완료까지 계속 보임 — 이 타이머는 "최소" 시간만 보장).
  // 80차 후속: 최종적으로 보장되는 최소 시간은 이 2초가 아니라 AppInner의
  // useMinSplashHold(3000)(PICK 광고 노출용) — 이 타이머는 폰트로딩 단계만의 완화된
  // 하한선이라 그대로 두고 안 올림(어차피 AppInner 게이트가 더 엄격해 항상 이걸 덮음).
  // 오늘 웹페이지 개선 요청으로 추가: 이 타이머가 AppInner의 useMinSplashHold보다 더
  // 느슨해야 한다는 전제(주석 참고)가 위에서 웹만 1200ms로 낮추며 깨졌음 — 이대로 두면
  // 이 2초가 오히려 병목이 되어 웹 단축이 무효화됨. 웹만 800ms로 함께 낮춤(원래 이
  // 타이머가 막으려던 "한 프레임만 그려지고 사라짐" 문제엔 여전히 충분).
  const [minSplashElapsed, setMinSplashElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinSplashElapsed(true), Platform.OS === 'web' ? 800 : 2000);
    return () => clearTimeout(t);
  }, []);

  // 78차 37차: 이 컴포넌트가 처음 커밋되는 시점엔 이미 아래 SplashLoadingScreen(네이티브
  // 스플래시와 동일한 배경색/로고)이 화면에 그려진 뒤이므로, 여기서 네이티브 스플래시를
  // 내려도 그 아래 이미 같은 그림이 있어 프레임 갭 없이 자연스럽게 이어짐.
  useEffect(() => {
    SplashScreen.hideAsync().catch(() => {});
  }, []);

  // 78차 40차: 웹 버전이 데스크톱 브라우저 창 전체 폭으로 늘어나 보이던 것을 지적받아,
  // 웹에서만 화면 폭을 75%로 제한하고 가운데 정렬 — 네이티브(iOS/Android)는 변화 없음.
  // 바깥 여백은 body 배경도 앱 배경색과 맞춰야 흰 여백 없이 자연스러움.
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.body.style.backgroundColor = COLORS.bg;
    }
  }, []);

  // 78차 72차: 모바일 브라우저에서 하단 탭바가 잘려 보이는 버그 — html/body/#root의
  // 실제 높이가 RN 웹의 height:'100%'(WebFrame)를 거쳐 결국 `100vh`로 귀결되는데,
  // 모바일 브라우저는 주소창이 보이는 초기 로드 시점 기준으로 100vh를 계산해서 실제
  // 화면보다 크게 잡는 경우가 많음(스크롤해서 주소창이 접혀도 레이아웃은 이미 그 큰
  // 높이로 고정) — 그만큼 하단 탭바가 실제 보이는 영역 아래로 밀려나 잘려 보였음.
  // 주소창 표시 상태를 실시간 반영하는 100dvh(동적 뷰포트 높이)를 폴백으로 추가해
  // 지원 브라우저에서 항상 실제 보이는 영역에 맞게 그려지도록 함(미지원 브라우저는
  // 기존 100vh로 자연히 폴백).
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const style = document.createElement('style');
      style.textContent = `html, body, #root { height: 100vh; height: 100dvh; }`;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, []);

  // 78차 46차: 새 창(Modal)이 화면 전체 폭(100%)으로 뜨는 버그 — react-native-web의 Modal은
  // 우리 컴포넌트 트리 안이 아니라 document.body에 직접 새 div를 붙이는 포탈 방식이라
  // (ModalPortal.js), 위 75% 폭 제한(App() 최상위 View 스타일)이 전혀 안 먹혔음. 개별
  // 화면마다 모달 스타일을 고치는 대신, body의 모든 직계 자식(#root + 모달 포탈 div들)에
  // 공통으로 적용되는 CSS 규칙 하나로 근본 해결 — 새로 추가되는 모달에도 자동 적용됨.
  // 78차 48차: 위 규칙이 "매장 수정"/"계약 이력" 등 일부 모달엔 안 먹히던 버그의 진짜 원인 —
  // react-native-web의 ModalContent가 실제 오버레이에 position:fixed+top/right/bottom/left:0을
  // 쓰는데, fixed는 조상의 max-width와 무관하게 항상 뷰포트 기준으로 크기가 정해진다(단,
  // 조상 중 하나가 transform 등으로 "포함 블록"을 새로 만들면 그 조상 기준으로 바뀜).
  // 포탈 wrapper div에 transform을 줘서 그 자식(고정 위치 오버레이)의 포함 블록 자체를
  // wrapper로 만들어버리는 방식으로 해결.
  // 78차 52차: 그 수정이 새 버그를 만들었음 — wrapper에 준 height:100vh가 일반 문서 흐름
  // (position 기본값 static)에 그대로 얹혀서, body 안에 "화면 전체 높이짜리 투명 블록"이
  // 매 모달마다(AppAlertHost처럼 항상 마운트돼있는 모달도 포함) 하나씩 layout에 실제로
  // 자리를 차지해 버렸음 — 매장 수정 모달을 닫고 나면 body의 스크롤 가능 총 높이가 실제
  // 콘텐츠보다 훨씬 커져, 화면이 아래로 붕 뜬 것처럼 보이던 버그의 원인. wrapper 자체도
  // position:fixed(+inset:0)로 만들어 문서 흐름에서 완전히 빼면서, 여전히 자식의 포함
  // 블록 역할은 transform으로 유지 — fixed 요소는 height:100vh 없이도 위/아래가 뷰포트
  // 기준으로 꽉 차므로 별도 높이 지정이 필요 없음.
  // 78차 53차: 그 수정이 또 다른, 훨씬 심각한 버그를 만들었음 — react-native-web의
  // ModalPortal은 <Modal visible={false}>여도 body에 div 자체는 항상 미리 붙여두고
  // (ModalAnimation이 내부적으로 null을 렌더해 "비어있는" 채로만 둠), visible이 true가 될
  // 때만 그 안에 실제 오버레이를 그린다. 이 화면(SuperAdminStoresScreen 등)처럼 여러
  // Modal을 JSX에 항상 렌더해두고 visible prop만 토글하는 화면은, 모달을 한 번도 안 열어도
  // 그 "빈" wrapper div가 body에 이미 존재 — 그런데 위 규칙이 이 빈 wrapper에도
  // position:fixed+inset:0을 걸어버려서 눈에는 안 보이지만 화면 전체를 뒤덮는 투명한
  // 클릭 차단막이 생겨버렸음(총관리자 매장관리 탭이 "멈춘 것처럼" 안 눌리던 원인 —
  // 실제로는 정지가 아니라 이 투명 오버레이가 모든 클릭을 가로채고 있었던 것).
  // :not(:empty) 추가로 "지금 실제로 내용이 렌더된(=열려있는) 모달"에만 이 규칙이
  // 적용되게 함 — 닫혀있는 모달의 빈 wrapper는 그대로 무해한 크기 0 요소로 남는다.
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const style = document.createElement('style');
      // #root 자체는 제외(:not(#root)) — #root는 이미 WebFrame이 내부 자식에 인라인 스타일로
      // 75%를 적용 중이라, 여기서 #root에도 또 max-width를 걸면 75%의 75%로 이중 축소됨.
      // WebFrame과 동일한 768px 기준 — 모바일 브라우저에서는 폭 제한을 안 걸어야 함.
      style.textContent = `@media (min-width: 768px) { body > div:not(#root):not(:empty) { position: fixed; inset: 0; max-width: 75%; margin-left: auto; margin-right: auto; transform: translateZ(0); } }`;
      document.head.appendChild(style);
      return () => document.head.removeChild(style);
    }
  }, []);

  // 80차 계속: 웹 배포본(unlockapp-dev.web.app 등) 방문 기록 — 매장 유치 이메일에 데모
  // 링크(?ref=매장명 등)를 첨부했을 때 실제로 클릭했는지/누가 왔는지 식별하기 위함.
  // 페이지 로드 시 1회만 fire-and-forget 호출(응답을 기다리지 않아 렌더링에 영향 없음,
  // 실패해도 무음 처리). functions/index.js의 logSiteVisitCF(onRequest, 인증 불필요) 참고.
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      fetch('https://asia-northeast3-roomescape-80d4d.cloudfunctions.net/logSiteVisitCF', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path: window.location.pathname,
          query: window.location.search,
          referrer: document.referrer,
          screenWidth: window.screen?.width,
          screenHeight: window.screen?.height,
          lang: navigator.language,
        }),
      }).catch(() => {});
    }
  }, []);

  // 80차 후속: SplashLoadingScreen이 더 이상 커스텀 폰트에 의존하지 않도록 바뀌어서
  // (fontWeight만으로 항상 동일하게 그림) fontsReady prop 자체가 필요 없어짐 — 웹에서
  // 폰트 다운로드 타이밍에 따라 글꼴이 화면에 뜬 채로 바뀌던 버그의 근본 원인 제거.
  // 80차 후속: [버그 수정] 여기서 fontsLoaded/minSplashElapsed로 조기 return하던 것을
  // 제거 — AppInner의 단일 게이트로 이관(위 주석 참고). 이제 App()은 로딩 상태와 무관하게
  // 항상 같은 트리(WebFrame 포함)를 렌더.
  const app = (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <AuthProvider>
          {/* 44차: 최상위 ErrorBoundary — 예기치 못한 크래시로 앱 전체 다운 방지 */}
          <ErrorBoundary>
            <AppInner fontsReady={fontsLoaded && minSplashElapsed} />
          </ErrorBoundary>
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );

  if (Platform.OS === 'web') {
    return <WebFrame>{app}</WebFrame>;
  }
  return app;
}

// 78차 40차 후속: 모바일 브라우저에서 확인해보니 75% 고정폭이 폰 화면(예: 390px)에선
// 실사용 영역을 292px까지 줄여버려 PICK·공지 라벨 줄바꿈/하단 탭 라벨 잘림 등 오히려
// 더 망가짐 — 데스크톱 폭(768px 이상)일 때만 75%로 제한하고, 그 아래(폰 브라우저)는
// 기존처럼 창 전체 폭을 그대로 사용.
// 80차: dev 웹 프리뷰(dev.unlockapp.co.kr)에서 데스크톱 브라우저로도 실제 폰 폭을
// 픽셀 단위로 확인하고 싶을 때 매번 개발자도구 기기 툴바를 켜지 않아도 되도록,
// URL에 ?mobile=1을 붙이면 고정 402px(디자인 목업의 iPhone 프레임 폭과 동일) 폭으로
// 강제 고정하는 모드 추가. 별도 서브도메인/배포 없이 같은 dev 빌드에서 링크만 다르게.
function useForcedMobileFrame() {
  const [forced, setForced] = useState(false);
  useEffect(() => {
    try {
      setForced(new URLSearchParams(window.location.search).get('mobile') === '1');
    } catch (_) {}
  }, []);
  return forced;
}

function WebFrame({ children }) {
  const { width } = useWindowDimensions();
  const forceMobile = useForcedMobileFrame();
  const isDesktop = width >= 768;
  if (forceMobile) {
    return <View style={webStyles.mobileFrame}>{children}</View>;
  }
  return (
    <View style={isDesktop ? webStyles.frame : webStyles.full}>{children}</View>
  );
}

const webStyles = StyleSheet.create({
  // RN 웹의 #root는 flexDirection:'row'라 flex:1+alignSelf:center 조합은(flexBasis:0이
  // width를 덮어써서) 가로 폭 제한이 전혀 안 먹었음 — margin:auto로 직접 가운데 정렬.
  frame: { width: '75%', height: '100%', marginLeft: 'auto', marginRight: 'auto', backgroundColor: COLORS.bg },
  full: { width: '100%', height: '100%', backgroundColor: COLORS.bg },
  mobileFrame: { width: 402, height: '100%', marginLeft: 'auto', marginRight: 'auto', backgroundColor: COLORS.bg },
});